import React, { useState, useEffect } from 'react';
import { LogOut, Plus, Users, MapPin, Search } from 'lucide-react';
import { supabase } from './supabaseClient';

export default function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('feed');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  
  const [myCities, setMyCities] = useState([]);
  const [mySpots, setMySpots] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [networkSpots, setNetworkSpots] = useState([]);
  
  const [newCity, setNewCity] = useState('');
  const [newSpot, setNewSpot] = useState({ city: '', name: '', category: 'restaurant', note: '' });
  const [friendEmail, setFriendEmail] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [showAddSpot, setShowAddSpot] = useState(false);

  const colors = {
    sage: '#7FAA92',
    sageLight: '#E8F0EC',
    sageText: '#2D5F4C',
    orange: '#D97757',
    orangeLight: '#FFF4F0'
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadUserData();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadUserData();
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadUserData = async () => {
    await Promise.all([loadMyCities(), loadMySpots(), loadFriends(), loadFriendRequests(), loadNetworkSpots()]);
  };

  const loadMyCities = async () => {
    const { data } = await supabase.from('user_cities').select('*').order('city');
    if (data) setMyCities(data);
  };

  const loadMySpots = async () => {
    const { data } = await supabase.from('recommendations').select('*').order('created_at', { ascending: false });
    if (data) setMySpots(data.filter(s => s.user_id === user?.id));
  };

  const loadFriends = async () => {
    const { data } = await supabase.from('friends').select('*').eq('status', 'accepted');
    if (data) setFriends(data);
  };

  const loadFriendRequests = async () => {
    const { data } = await supabase.from('friends').select('*').eq('status', 'pending');
    if (data) setFriendRequests(data.filter(r => r.friend_id === user?.id));
  };

  const loadNetworkSpots = async () => {
    const { data: friendData } = await supabase.from('friends').select('*').eq('status', 'accepted');
    if (!friendData) return;
    
    const friendIds = friendData.map(f => f.user_id === user?.id ? f.friend_id : f.user_id);
    if (friendIds.length === 0) return;
    
    const { data: spots } = await supabase.from('recommendations').select('*, profiles(name, email)').in('user_id', friendIds);
    if (spots) setNetworkSpots(spots);
  };

  const handleSignUp = async () => {
    const { data, error } = await supabase.auth.signUp({
      email: authEmail,
      password: authPassword,
      options: { data: { name: authName } }
    });
    if (!error && data.user) {
      await supabase.from('profiles').insert([{ id: data.user.id, email: authEmail.toLowerCase(), name: authName }]);
      setShowAuthModal(false);
      resetAuth();
    }
  };

  const handleLogin = async () => {
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
    if (!error) {
      setShowAuthModal(false);
      resetAuth();
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMyCities([]);
    setMySpots([]);
    setFriends([]);
    setNetworkSpots([]);
  };

  const resetAuth = () => {
    setAuthEmail('');
    setAuthPassword('');
    setAuthName('');
  };

  const addCity = async () => {
    if (!newCity.trim()) return;
    await supabase.from('user_cities').insert([{ user_id: user.id, city: newCity.trim() }]);
    setNewCity('');
    loadMyCities();
  };

  const removeCity = async (id) => {
    await supabase.from('user_cities').delete().eq('id', id);
    loadMyCities();
  };

  const addSpot = async () => {
    if (!newSpot.city.trim() || !newSpot.name.trim()) return;
    await supabase.from('recommendations').insert([{
      user_id: user.id,
      city: newSpot.city.trim(),
      name: newSpot.name.trim(),
      category: newSpot.category,
      description: newSpot.note.trim()
    }]);
    setNewSpot({ city: '', name: '', category: 'restaurant', note: '' });
    setShowAddSpot(false);
    loadMySpots();
    loadNetworkSpots();
  };

  const removeSpot = async (id) => {
    await supabase.from('recommendations').delete().eq('id', id);
    loadMySpots();
  };

  const sendFriendRequest = async () => {
    if (!friendEmail.trim()) return;
    const { data: found } = await supabase.from('profiles').select('id').eq('email', friendEmail.trim().toLowerCase()).single();
    if (!found) { alert('User not found'); return; }
    if (found.id === user.id) { alert("That's you!"); return; }
    await supabase.from('friends').insert([{ user_id: user.id, friend_id: found.id, status: 'pending' }]);
    setFriendEmail('');
    alert('Friend request sent!');
  };

  const acceptRequest = async (id) => {
    await supabase.from('friends').update({ status: 'accepted' }).eq('id', id);
    loadFriends();
    loadFriendRequests();
    loadNetworkSpots();
  };

  const filteredSpots = searchCity 
    ? networkSpots.filter(s => s.city.toLowerCase().includes(searchCity.toLowerCase()))
    : networkSpots;

  const categoryEmoji = { restaurant: '🍽️', bar: '🍸', coffee: '☕', activity: '🎯', hidden_gem: '💎', hotel: '🏨', neighborhood: '🏘️' };

  if (!user) {
    return (
      <div style={{minHeight: '100vh', background: colors.sageLight, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem'}}>
        <h1 style={{fontSize: '2.5rem', fontWeight: 'bold', color: colors.sageText, marginBottom: '0.5rem'}}>Wanderous</h1>
        <p style={{color: '#6B7280', marginBottom: '2rem', textAlign: 'center'}}>Local tips from people you trust</p>
        <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} style={{padding: '1rem 2rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '9999px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1rem', width: '200px'}}>Sign In</button>
        <button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} style={{padding: '1rem 2rem', background: 'white', color: colors.sage, border: `2px solid ${colors.sage}`, borderRadius: '9999px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', width: '200px'}}>Create Account</button>
        
        {showAuthModal && (
          <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'}} onClick={() => setShowAuthModal(false)}>
            <div style={{background: 'white', borderRadius: '1.5rem', padding: '2rem', maxWidth: '400px', width: '100%'}} onClick={e => e.stopPropagation()}>
              <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: colors.sageText}}>{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
              {authMode === 'signup' && <input type="text" placeholder="Your Name" value={authName} onChange={e => setAuthName(e.target.value)} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '1rem', fontSize: '1rem', boxSizing: 'border-box'}} />}
              <input type="email" placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '1rem', fontSize: '1rem', boxSizing: 'border-box'}} />
              <input type="password" placeholder="Password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '1rem', fontSize: '1rem', boxSizing: 'border-box'}} />
              <button onClick={authMode === 'login' ? handleLogin : handleSignUp} style={{width: '100%', padding: '0.875rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '9999px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1rem'}}>{authMode === 'login' ? 'Sign In' : 'Create Account'}</button>
              <p style={{textAlign: 'center', color: '#6B7280', fontSize: '0.875rem'}}>{authMode === 'login' ? "Don't have an account? " : "Already have an account? "}<span onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')} style={{color: colors.sage, cursor: 'pointer', fontWeight: 600}}>{authMode === 'login' ? 'Sign up' : 'Sign in'}</span></p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: '#F9FAFB'}}>
      {/* Header */}
      <div style={{background: 'white', borderBottom: '1px solid #E5E7EB', padding: '1rem', position: 'sticky', top: 0, zIndex: 50}}>
        <div style={{maxWidth: '600px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h1 style={{fontSize: '1.25rem', fontWeight: 'bold', color: colors.sageText}}>Wanderous</h1>
          <button onClick={handleLogout} style={{background: colors.orangeLight, color: colors.orange, padding: '0.5rem', borderRadius: '50%', border: 'none', cursor: 'pointer'}}><LogOut style={{width: '1.25rem', height: '1.25rem'}} /></button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{maxWidth: '600px', margin: '0 auto', padding: '1rem'}}>
        
        {/* Nav Tabs */}
        <div style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: 'white', padding: '0.5rem', borderRadius: '1rem'}}>
          {[{id: 'feed', icon: Search, label: 'Discover'}, {id: 'my-spots', icon: MapPin, label: 'My Spots'}, {id: 'friends', icon: Users, label: 'Friends'}].map(tab => (
            <button key={tab.id} onClick={() => setView(tab.id)} style={{flex: 1, padding: '0.75rem', background: view === tab.id ? colors.sage : 'transparent', color: view === tab.id ? 'white' : '#6B7280', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 500}}>
              <tab.icon style={{width: '1rem', height: '1rem'}} />{tab.label}
            </button>
          ))}
        </div>

        {/* Feed View */}
        {view === 'feed' && (
          <div>
            <div style={{marginBottom: '1rem'}}>
              <input type="text" placeholder="Search by city..." value={searchCity} onChange={e => setSearchCity(e.target.value)} style={{width: '100%', padding: '0.875rem', border: '2px solid #E5E7EB', borderRadius: '1rem', fontSize: '1rem', boxSizing: 'border-box'}} />
            </div>
            {filteredSpots.length === 0 ? (
              <div style={{textAlign: 'center', padding: '3rem', color: '#6B7280'}}>
                {friends.length === 0 ? 'Add friends to see their recommendations!' : searchCity ? 'No spots found for that city' : 'No recommendations from your network yet'}
              </div>
            ) : (
              filteredSpots.map(spot => (
                <div key={spot.id} style={{background: 'white', padding: '1rem', borderRadius: '1rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div>
                      <div style={{fontWeight: 600, color: '#1F2937'}}>{categoryEmoji[spot.category] || '📍'} {spot.name}</div>
                      <div style={{fontSize: '0.875rem', color: colors.sage}}>{spot.city}</div>
                      {spot.description && <div style={{fontSize: '0.875rem', color: '#6B7280', marginTop: '0.5rem'}}>{spot.description}</div>}
                    </div>
                  </div>
                  <div style={{fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.5rem'}}>from {spot.profiles?.name || 'a friend'}</div>
                </div>
              ))
            )}
          </div>
        )}

        {/* My Spots View */}
        {view === 'my-spots' && (
          <div>
            {/* My Cities */}
            <div style={{background: 'white', padding: '1rem', borderRadius: '1rem', marginBottom: '1rem'}}>
              <h3 style={{fontWeight: 600, color: colors.sageText, marginBottom: '0.75rem'}}>Cities I Know</h3>
              <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.75rem'}}>
                <input type="text" placeholder="Add a city..." value={newCity} onChange={e => setNewCity(e.target.value)} onKeyPress={e => e.key === 'Enter' && addCity()} style={{flex: 1, padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', fontSize: '0.875rem'}} />
                <button onClick={addCity} style={{padding: '0.75rem 1rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: 600}}>Add</button>
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                {myCities.map(c => (
                  <span key={c.id} style={{background: colors.sageLight, color: colors.sageText, padding: '0.5rem 0.75rem', borderRadius: '9999px', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    {c.city} <button onClick={() => removeCity(c.id)} style={{background: 'none', border: 'none', cursor: 'pointer', color: colors.sage}}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Add Spot Button */}
            <button onClick={() => setShowAddSpot(true)} style={{width: '100%', padding: '1rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '1rem', cursor: 'pointer', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}>
              <Plus style={{width: '1.25rem', height: '1.25rem'}} /> Add a Spot
            </button>

            {/* My Spots List */}
            {mySpots.map(spot => (
              <div key={spot.id} style={{background: 'white', padding: '1rem', borderRadius: '1rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                <div>
                  <div style={{fontWeight: 600, color: '#1F2937'}}>{categoryEmoji[spot.category] || '📍'} {spot.name}</div>
                  <div style={{fontSize: '0.875rem', color: colors.sage}}>{spot.city}</div>
                  {spot.description && <div style={{fontSize: '0.875rem', color: '#6B7280', marginTop: '0.25rem'}}>{spot.description}</div>}
                </div>
                <button onClick={() => removeSpot(spot.id)} style={{background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '1.25rem'}}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Friends View */}
        {view === 'friends' && (
          <div>
            {/* Add Friend */}
            <div style={{background: 'white', padding: '1rem', borderRadius: '1rem', marginBottom: '1rem'}}>
              <h3 style={{fontWeight: 600, color: colors.sageText, marginBottom: '0.75rem'}}>Add a Friend</h3>
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <input type="email" placeholder="Friend's email..." value={friendEmail} onChange={e => setFriendEmail(e.target.value)} style={{flex: 1, padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', fontSize: '0.875rem'}} />
                <button onClick={sendFriendRequest} style={{padding: '0.75rem 1rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: 600}}>Add</button>
              </div>
            </div>

            {/* Friend Requests */}
            {friendRequests.length > 0 && (
              <div style={{background: colors.orangeLight, padding: '1rem', borderRadius: '1rem', marginBottom: '1rem'}}>
                <h3 style={{fontWeight: 600, color: colors.orange, marginBottom: '0.75rem'}}>Friend Requests</h3>
                {friendRequests.map(r => (
                  <div key={r.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0'}}>
                    <span>New request</span>
                    <button onClick={() => acceptRequest(r.id)} style={{padding: '0.5rem 1rem', background: colors.orange, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600}}>Accept</button>
                  </div>
                ))}
              </div>
            )}

            {/* Friends List */}
            <div style={{background: 'white', padding: '1rem', borderRadius: '1rem'}}>
              <h3 style={{fontWeight: 600, color: colors.sageText, marginBottom: '0.75rem'}}>My Friends ({friends.length})</h3>
              {friends.length === 0 ? (
                <p style={{color: '#6B7280', fontSize: '0.875rem'}}>Add friends to see their local tips!</p>
              ) : (
                friends.map(f => (
                  <div key={f.id} style={{padding: '0.5rem 0', borderBottom: '1px solid #E5E7EB'}}>
                    <span style={{color: '#1F2937'}}>Friend connected</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Spot Modal */}
      {showAddSpot && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 100}} onClick={() => setShowAddSpot(false)}>
          <div style={{background: 'white', borderRadius: '1.5rem', padding: '1.5rem', maxWidth: '400px', width: '100%'}} onClick={e => e.stopPropagation()}>
            <h2 style={{fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: colors.sageText}}>Add a Spot</h2>
            <input type="text" placeholder="City" value={newSpot.city} onChange={e => setNewSpot({...newSpot, city: e.target.value})} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '0.75rem', fontSize: '0.875rem', boxSizing: 'border-box'}} />
            <input type="text" placeholder="Name of place" value={newSpot.name} onChange={e => setNewSpot({...newSpot, name: e.target.value})} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '0.75rem', fontSize: '0.875rem', boxSizing: 'border-box'}} />
            <select value={newSpot.category} onChange={e => setNewSpot({...newSpot, category: e.target.value})} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '0.75rem', fontSize: '0.875rem', background: 'white'}}>
              <option value="restaurant">Restaurant</option>
              <option value="bar">Bar</option>
              <option value="coffee">Coffee</option>
              <option value="activity">Activity</option>
              <option value="hidden_gem">Hidden Gem</option>
              <option value="hotel">Hotel</option>
              <option value="neighborhood">Neighborhood</option>
            </select>
            <textarea placeholder="Why you love it (optional)" value={newSpot.note} onChange={e => setNewSpot({...newSpot, note: e.target.value})} style={{width: '100%', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '0.75rem', marginBottom: '1rem', fontSize: '0.875rem', minHeight: '80px', resize: 'vertical', boxSizing: 'border-box'}} />
            <button onClick={addSpot} style={{width: '100%', padding: '0.875rem', background: colors.sage, color: 'white', border: 'none', borderRadius: '9999px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer'}}>Add Spot</button>
          </div>
        </div>
      )}
    </div>
  );
}
